import { OrderHistory } from '@/model/blotters';
import { formatDateTime, formatLocalDateTime } from '@/utils/format';
import { getInstrumentFormatter, getUniversalInstrument } from '@/utils/hooks/useInstrument';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import { getAssetName, translatePair } from '@/utils/symbolMapping';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';
import { orderStatusColorCell } from './orderBlotterTableHelper';

export const historyColumns = (lang: UseLanguageReturn, generalSettings): ColumnDef<OrderHistory>[] => {
    return [
        { accessorKey: 'orderId', header: lang.blotters.orderId, size: 160 },
        { accessorKey: 'time', header: `${lang.blotters.time} (${generalSettings.timezone.value})`, size: 150 },
        { accessorKey: 'pair', header: lang.blotters.pair, size: 100 },
        { accessorKey: 'ccy', header: lang.blotters.ccy, size: 60 },
        { accessorKey: 'orderType', header: lang.blotters.orderType, size: 100 },
        {
            accessorKey: 'status',
            header: lang.blotters.status,
            size: 120,
            cell: (context: CellContext<OrderHistory, any>) => {
                const { getValue } = context;
                return <span className={orderStatusColorCell(context)}>{getValue()}</span>;
            }
        },
        {
            accessorKey: 'executionType',
            header: lang.blotters.executionType,
            size: 120,
            cell: (context: CellContext<OrderHistory, any>) => {
                const { getValue } = context;
                return <span className={orderStatusColorCell(context)}>{getValue()}</span>;
            }
        },
        {
            accessorKey: 'side',
            header: lang.blotters.side,
            size: 60,
            cell: ({ row, getValue }: CellContext<OrderHistory, any>) => {
                const value = getValue();
                return (
                    <div
                        className={cn('text-sm', {
                            'text-brand-green': value === 'BUY',
                            'text-brand-red': value === 'SELL'
                        })}>
                        {value}
                    </div>
                );
            }
        },
        { accessorKey: 'qty', header: lang.blotters.qty, size: 80 },
        { accessorKey: 'price', header: lang.blotters.price, size: 80 },
        { accessorKey: 'lastPrice', header: lang.blotters.lastPrice, size: 80 },
        { accessorKey: 'lastQty', header: lang.blotters.lastQty, size: 80 },
        { accessorKey: 'avgPrice', header: lang.blotters.avgPrice, size: 80 },
        { accessorKey: 'stopPrice', header: lang.blotters.stopPrice, size: 80 },
        { accessorKey: 'cumQty', header: lang.blotters.cumQty, size: 80 },
        { accessorKey: 'leavesQty', header: lang.blotters.leavesQty, size: 90 },
        { accessorKey: 'tif', header: lang.blotters.tif, size: 60 },
        { accessorKey: 'account', header: lang.blotters.account, size: 100 },
        { accessorKey: 'user', header: lang.blotters.user, size: 100 }
    ];
};

export const mapOrderHistoryBlotters = (trade, locale, credentials, formatDate, isUTC) => {
    const { formatPrice, formatAvgPrice } = getInstrumentFormatter(locale, trade.pair);
    const executionTime =
        trade.executionTime &&
        (isUTC
            ? formatDateTime(formatDate, trade.executionTime)
            : formatLocalDateTime(formatDate, trade.executionTime));

    let tableInstrument = translatePair(trade.pair);
    const instrumentConfig = getUniversalInstrument(trade.pair);

    if (instrumentConfig?.type === 'Future') tableInstrument = instrumentConfig.gcexSymbol;

    return {
        ...trade,
        time: executionTime,
        price: formatPrice(trade.price),
        lastPrice: formatPrice(trade.lastPrice),
        avgPrice: formatAvgPrice(trade.avgPrice),
        stopPrice: formatPrice(trade.stopPrice),
        pair: tableInstrument,
        ccy: getAssetName(trade.ccy),
        ccy1: getAssetName(trade.ccy1),
        ccy2: getAssetName(trade.ccy2)
    };
};
